import { EntityTypes } from '../types/shared.types';

/**
 * How the history writer (apps/service-history) records one mutable POS-table
 * entity. See AGENTS.md §4.4.1 and docs/plans/concurrency-safe-writes.md §4.
 */
export interface HistoryEntityConfig {
  /** Snapshot attributes tried in order for `entity_label` (e.g. `name`). */
  labelFields: string[];
  /**
   * Fields the system changes on its own (e.g. store product `inventory`
   * after a sale). A change touching only these fields is AUTOMATIC and is
   * hidden from the Activity Timeline by default.
   */
  automaticFields?: string[];
  /**
   * The numeric attribute the versioned-write helper increments. Default
   * `version`; debts use `row_version` because their `version` is a string
   * change-log id.
   */
  versionField?: string;
  /**
   * The attribute holding the merchant id when it is not `merchant_id`
   * (the merchant row itself keeps it in `id`). Scopes the timeline.
   */
  merchantIdField?: string;
  /**
   * Telemetry fields refreshed constantly (a POS heartbeat every minute).
   * A versioned change touching only these is not recorded.
   */
  ignoredFields?: string[];
  /**
   * True when creating the entity is itself automatic (a stock row appears
   * with a product's first stock movement). Creates have no changed fields,
   * so `automaticFields` cannot classify them.
   */
  automaticCreate?: boolean;
  /**
   * Items whose `field` equals `value` are a person's own records (a manual
   * task): their changes are MANUAL whatever fields they touch.
   */
  manualIf?: { field: string; value: string };
  /**
   * Credentials (PINs, passwords). Never stored in history: dropped from the
   * snapshot, and a change to one records who changed it, not the values.
   */
  redactedFields?: string[];
  /**
   * Attributes dropped at any depth (e.g. `lines[].unit_cost`) from the
   * snapshot and the recorded changes: values only some users may see
   * (costs need `orders:costs.view`), which the Activity Timeline cannot
   * filter per user.
   */
  deepRedactedFields?: string[];
}

/**
 * Mutable-entity allowlist: only these `EntityIndexPK` values reach the
 * history table. Add an entity in the same PR that moves all its writers to
 * `buildVersionedUpdate` / `buildVersionedCreate`.
 */
export const HISTORY_ENTITIES: Partial<
  Record<EntityTypes, HistoryEntityConfig>
> = {
  // Phase 1: stock and valuation.
  [EntityTypes.PRODUCT]: { labelFields: ['name'] },
  [EntityTypes.PRODUCT_FRACTION]: { labelFields: ['fraction_name'] },
  [EntityTypes.STORE_PRODUCT]: {
    labelFields: ['name'],
    // Moved by every sale's stock sync.
    automaticFields: [
      'inventory',
      'stock_breakdown',
      'stock_version',
      'popularity_score',
      'is_available_for_sale',
    ],
  },
  [EntityTypes.CURRENT_INVENTORY]: {
    labelFields: ['product_name'],
    automaticCreate: true,
    // Stock movements; who counted or received shows on the source record.
    automaticFields: [
      'cumulative_quantity',
      'inventory_value_change',
      'retail_value_change',
      'change_quantity',
      'change_fraction',
      'processed_at',
      'product_name',
    ],
  },

  // Purchase-order expiry warnings (service-purchase-orders).
  [EntityTypes.PRODUCT_EXPIRY]: {
    labelFields: ['product_name'],
    // Stamped by the daily summary job.
    automaticFields: ['notified_at'],
  },

  // Integrations with other POS systems (service-integrations).
  [EntityTypes.INTEGRATION_CONNECTION]: {
    labelFields: ['qb_merchant_name', 'provider'],
    redactedFields: ['partner_token'],
  },
  [EntityTypes.INTEGRATION_MAPPINGS]: { labelFields: ['merchant_id'] },
  [EntityTypes.INTEGRATION_PRODUCT_LINK]: {
    labelFields: ['qb_item_number', 'qb_list_id'],
  },
  [EntityTypes.INTEGRATION_IMPORT_JOB]: {
    labelFields: ['id'],
    // Progress the worker moves as each family is imported.
    automaticFields: [
      'status',
      'done',
      'failed',
      'skipped',
      'errors',
      'started_at',
      'finished_at',
      'next_index',
    ],
  },

  // Phase 2: money and balances.
  [EntityTypes.CUSTOMER]: {
    labelFields: ['name'],
    // Loyalty moved by sales and redeems, and the cached debt summary.
    automaticFields: [
      'total_points',
      'total_spent',
      'total_visits',
      'last_visit',
      'debtor_summary',
    ],
    redactedFields: ['pin'],
  },
  [EntityTypes.DEBT]: {
    labelFields: ['debtor_name', 'id'],
    // `version` is the debt's change-log id; the helper's counter is here.
    versionField: 'row_version',
    // Moved by every payment; the payment row records who paid.
    automaticFields: [
      'outstanding_balance',
      'last_payment_date',
      'next_due_date',
      'closed_date',
    ],
  },
  [EntityTypes.DEBT_PAYMENT]: {
    labelFields: ['payer_name'],
    // Stamped when the payment is reversed; the reversal is its own row.
    automaticFields: ['reversed_by_payment_id'],
  },
  [EntityTypes.ACCOUNT_TRANSFER_REQUEST]: {
    labelFields: ['transfer_id'],
    // The approval claim is held only while the money moves.
    automaticFields: ['approval_claimed_at', 'approval_claimed_by_id'],
  },
  [EntityTypes.SHIFT]: {
    labelFields: ['opened_by_name', 'id'],
  },
  [EntityTypes.TOPUP_REQUEST]: {
    labelFields: ['transaction_id'],
  },
  [EntityTypes.WALLET_TRANSFER_OUT]: {
    labelFields: ['transaction_id'],
  },
  [EntityTypes.WALLET_MESSAGE]: {
    labelFields: ['receiver_number'],
  },
  [EntityTypes.INVOICE]: {
    labelFields: ['invoice_number', 'id'],
    // The collection claim is held only while wallets are debited.
    automaticFields: ['collection_claimed_at', 'collection_claim_id'],
  },
  [EntityTypes.PURCHASE_ORDER]: {
    labelFields: ['id'],
    // Totals every payment moves; the payment row records who paid.
    automaticFields: [
      'po_total_paid',
      'po_total_refunded',
      'po_amount_due',
      'payment_status',
    ],
  },
  [EntityTypes.PURCHASE_ORDER_PAYMENT]: {
    labelFields: ['purchase_order_id'],
    // Stamped when the payment is reversed; the reversal is its own row.
    automaticFields: ['reversed_by_payment_id'],
  },

  // Phase 3: operations and sales documents.
  [EntityTypes.RECEIPT]: {
    labelFields: ['receipt_number'],
    // Set by the receipts stream (refund links, automatic checks).
    automaticFields: ['failed_checks', 'associated_refunds'],
  },
  [EntityTypes.WEIGHTED_SALE]: {
    labelFields: ['product_name', 'id'],
  },
  [EntityTypes.INVENTORY_TRANSFER]: {
    labelFields: ['requesting_store_name', 'id'],
  },
  [EntityTypes.THRESHOLD]: {
    labelFields: ['store_id'],
  },
  [EntityTypes.INVENTORY_TEMPLATE]: {
    labelFields: ['title', 'id'],
  },
  [EntityTypes.DAILY_COUNT]: {
    labelFields: ['count_date', 'id'],
  },
  [EntityTypes.DISCOUNT]: {
    labelFields: ['name'],
    // Set by the expiry sweeper.
    automaticFields: [
      'expiry_processed_at',
      'expiry_attempt_count',
      'expiry_error',
      'expiry_failed_at',
    ],
  },
  [EntityTypes.PRICING_PLAN]: { labelFields: ['name'] },
  [EntityTypes.CUSTOMER_AGREEMENT]: { labelFields: ['plan_id', 'id'] },
  [EntityTypes.POS_DEVICE]: {
    labelFields: ['name', 'id'],
    // The heartbeat refreshes these every minute while a user holds it.
    ignoredFields: [
      'current_user_last_seen_at',
      'current_user_latitude',
      'current_user_longitude',
      'current_user_accuracy_meters',
      'current_user_location_source',
      'last_heartbeat_at',
      'last_session_event_at',
    ],
    // The install's possession proof for the offline routes.
    redactedFields: ['session_secret_hash'],
  },
  [EntityTypes.USER]: {
    labelFields: ['name', 'username'],
    // The PIN, also copied inside the Cognito attributes, and its offline
    // verifier.
    redactedFields: ['PIN', 'auth_user', 'pin_hash', 'pin_salt'],
  },
  [EntityTypes.GLOBAL_PRODUCT]: { labelFields: ['name'] },
  [EntityTypes.GLOBAL_PRODUCT_FRACTION]: {
    labelFields: ['fraction_name', 'fraction_id'],
  },
  [EntityTypes.MERCHANT]: {
    labelFields: ['business_name'],
    merchantIdField: 'id',
  },
  // Phase 4: security and notifications. (Roles, principals and
  // permissions live in their own table: versioned, not streamed.)
  [EntityTypes.NOTIFICATION_CONTACT]: { labelFields: ['name'] },
  [EntityTypes.NOTIFICATION_SCHEDULE]: {
    labelFields: ['type'],
    // Stamped by the scheduled generator after each run.
    automaticFields: ['last_run_at', 'last_period_key'],
  },
  // Phase 5: configuration.
  [EntityTypes.STORE]: { labelFields: ['name'] },
  [EntityTypes.STORE_SCALE_CONFIG]: { labelFields: ['scale_type'] },
  [EntityTypes.STORE_SCALE_SHORTCUT]: { labelFields: ['name', 'plu'] },
  [EntityTypes.STORE_FAVOURITE_CELL]: { labelFields: ['product_id'] },
  [EntityTypes.TAX]: { labelFields: ['name'] },
  [EntityTypes.SUPPLIER]: { labelFields: ['name'] },
  [EntityTypes.PAYMENT_TYPE]: { labelFields: ['name'] },
  [EntityTypes.LOYALTY_PROGRAM]: { labelFields: ['name'] },
  [EntityTypes.NOTIFICATION_SUBSCRIPTION]: { labelFields: ['type'] },
  [EntityTypes.TOPUP_NUMBER]: { labelFields: ['phone_number'] },
  [EntityTypes.TOPUP_DEVICE]: {
    labelFields: ['device_label'],
    // Moved by every SMS a phone forwards while it waits for assignment.
    automaticFields: [
      'last_seen_at',
      'held_message_ids',
      'suggested_phone_number',
      'alerted_at',
    ],
    automaticCreate: true,
  },
  [EntityTypes.TOPUP_PASSWORD]: {
    labelFields: ['phone_number'],
    // Password hashes, current and previous.
    redactedFields: ['current_password', 'update_history'],
  },
  [EntityTypes.BILLING_RUN]: { labelFields: ['id'] },
  [EntityTypes.SALES_AGENT]: { labelFields: ['name'] },
  [EntityTypes.PLATFORM_PAYMENT_TYPE]: { labelFields: ['name'] },
  // Business finances (service-admin): platform-level, no store scope.
  [EntityTypes.PLATFORM_FOUNDER]: { labelFields: ['name'] },
  [EntityTypes.PLATFORM_EXPENSE]: { labelFields: ['provider'] },
  [EntityTypes.PLATFORM_INCOME]: { labelFields: ['source_name'] },
  // Settings → Roles visibility and order (service-admin): one global record.
  [EntityTypes.PERMISSION_VISIBILITY]: { labelFields: ['id'] },
  // Global WhatsApp on/off switch (service-notifications): one record.
  [EntityTypes.WHATSAPP_SETTINGS]: { labelFields: ['id'] },
  // Admin banners (service-admin): platform-level, no store scope.
  [EntityTypes.ANNOUNCEMENT]: { labelFields: ['id'] },
  // Orders & Transfers files (service-inventory-transfers): checked once on
  // confirm, never changed afterwards. The object key is not a secret.
  [EntityTypes.ATTACHMENT]: { labelFields: ['file_name', 'id'] },
  // A system task opens and closes with the work it tracks (§4.13); a
  // manual one is named by its title (its done note and withdrawal show).
  [EntityTypes.TASK]: {
    labelFields: ['title', 'type', 'id'],
    automaticFields: [
      'status',
      'params',
      'assignee_id',
      'waiting_since',
      'closed_at',
      'closed_by_id',
      'closed_by_name',
      'last_occurred_at',
    ],
    automaticCreate: true,
    manualIf: { field: 'origin', value: 'MANUAL' },
  },
  // Orders & Transfers PR 6 (service-inventory-transfers). The saga's
  // intents are bookkeeping of the stock moves, not a person's change.
  [EntityTypes.STOCK_ORDER]: {
    labelFields: ['reference', 'id'],
    // The ageing job's bookkeeping (the expiry itself is shown).
    automaticFields: ['reminded_at', 'ageing_done_at'],
  },
  [EntityTypes.STOCK_TRANSFER]: {
    labelFields: ['reference', 'id'],
    automaticFields: [
      'stock_operation_intents',
      'follow_up_since',
      'demand_return_pending',
      'tasks_pending',
    ],
    // Carried costs need orders:costs.view; the timeline cannot check it.
    deepRedactedFields: [
      'unit_cost',
      'result_unit_cost',
      'carried_unit_cost',
      'cost',
      'valuation_adjustment',
    ],
  },
};

export const getHistoryEntityConfig = (
  entityType: unknown,
): HistoryEntityConfig | undefined =>
  typeof entityType === 'string'
    ? HISTORY_ENTITIES[entityType as EntityTypes]
    : undefined;
